import React, { useCallback, useState } from 'react'

import { GENDER_OPTIONS } from 'constants/user'
import {
  ETHNICITY_OPTIONS,
  generateLabelValues,
  HEIGHT_OPTIONS,
  POLITICS_OPTIONS,
  RELIGION_OPTIONS,
  STAR_SIGN_OPTIONS,
} from 'constants/profile'

import { CloseIcon } from 'components'
import ProfileLabel from 'components/Profile/ProfileLabel'
import ProfileSelect from 'components/Profile/ProfileSelect'
import ProfileInput from 'components/Profile/ProfileInput'
import ProfileTextarea from 'components/Profile/ProfileTextarea'
import Button from 'components/Button'

const AGE_OPTIONS = generateLabelValues(Array.from({ length: 100 }, (_, i) => i + 18))
const H_NUM_OPTIONS = generateLabelValues(Array.from({ length: 10 }, (_, i) => i + 1))

const Input = ({ name, type, required, options, filters, attribute, onChange }) => {
  const params = {
    name,
    required,
    onChange,
    value: filters[attribute][name],
  }

  switch (type) {
    case 'select':
      return <ProfileSelect {...params} options={options} />
    case 'textarea':
      return <ProfileTextarea {...params} />
    default:
      return <ProfileInput {...params} type={type} />
  }
}

const filterGroups = [
  {
    name: 'About The User',
    sections: [
      {
        name: 'About',
        filters: [
          {
            label: 'Gender',
            name: 'gender',
            options: GENDER_OPTIONS,
            type: 'select',
            attribute: 'user',
          },
          {
            label: 'Min Age',
            name: 'minAge',
            options: AGE_OPTIONS,
            type: 'select',
            attribute: 'user',
          },
          {
            label: 'Max Age',
            name: 'maxAge',
            options: AGE_OPTIONS,
            type: 'select',
            attribute: 'user',
          },
          {
            label: 'Min Height',
            name: 'minHeight',
            options: HEIGHT_OPTIONS,
            type: 'select',
            attribute: 'profile',
          },
          {
            label: 'Max Height',
            name: 'maxHeight',
            options: HEIGHT_OPTIONS,
            type: 'select',
            attribute: 'profile',
          },
        ],
      },
      {
        name: 'Profile',
        filters: [
          {
            label: 'Religion',
            name: 'religion',
            options: RELIGION_OPTIONS,
            type: 'select',
            attribute: 'profile',
          },
          {
            label: 'Ethnicity',
            name: 'ethnicity',
            options: ETHNICITY_OPTIONS,
            type: 'select',
            attribute: 'profile',
          },
          {
            label: 'Politics',
            name: 'politics',
            options: POLITICS_OPTIONS,
            type: 'select',
            attribute: 'profile',
          },
          {
            label: 'Star Sign',
            name: 'starSign',
            options: STAR_SIGN_OPTIONS,
            type: 'select',
            attribute: 'profile',
          },
        ],
      },
      {
        name: 'H Number',
        filters: [
          {
            label: 'Min',
            name: 'minHNum',
            options: H_NUM_OPTIONS,
            type: 'select',
            attribute: 'user',
          },
          {
            label: 'Max',
            name: 'maxHNum',
            options: H_NUM_OPTIONS,
            type: 'select',
            attribute: 'user',
          },
        ],
      },
    ],
  },
  {
    name: 'What they are looking for',
    sections: [
      {
        name: 'Looking For',
        filters: [
          {
            label: 'Gender',
            name: 'genderPreference',
            options: GENDER_OPTIONS,
            type: 'select',
            attribute: 'user',
          },
          {
            label: 'Age',
            name: 'age',
            options: AGE_OPTIONS,
            type: 'select',
            attribute: 'preference',
          },
          {
            label: 'Height',
            name: 'height',
            options: HEIGHT_OPTIONS,
            type: 'select',
            attribute: 'preference',
          },
        ],
      },
      {
        name: '=================================',
        filters: [
          {
            label: 'Religion',
            name: 'religion',
            options: RELIGION_OPTIONS,
            type: 'select',
            attribute: 'preference',
          },
          {
            label: 'Ethnicity',
            name: 'ethnicity',
            options: ETHNICITY_OPTIONS,
            type: 'select',
            attribute: 'preference',
          },
          {
            label: 'Politics',
            name: 'politics',
            options: POLITICS_OPTIONS,
            type: 'select',
            attribute: 'preference',
          },
        ],
      },
    ],
  },
]

const defaultFilters = {
  user: {},
  profile: {},
  preference: {},
}

const SearchForm = ({ query, onClose, onSubmit }) => {
  const [filters, setFilters] = useState(query.filters || defaultFilters)
  const [search, setSearch] = useState(query.search || '')

  const handleFilterChange = useCallback(({ attribute, e }) => {
    const { name, value } = e.target

    setFilters(prev => ({
      ...prev,
      [attribute]: {
        ...prev[attribute],
        [name]: value,
      },
    }))
  }, [])

  const handleSubmit = useCallback(
    async e => {
      e.preventDefault()
      onSubmit({ filters, search })
    },
    [onSubmit, search, filters]
  )

  return (
    <div className="relative w-full p-5 max-w-full md:p-7 md:py-5 bg-black">
      <button className="bg-transparent text-white absolute top-3 right-4" onClick={onClose}>
        <CloseIcon className="w-4 h-4" />
      </button>
      <div className="text-white text-center text-3xl font-bold font-ownersWide mb-10 underline">
        Search
      </div>
      <form onSubmit={handleSubmit}>
        <div className="flex flex-wrap justify-center mb-10">
          <div className="w-96 mb-2">
            <ProfileInput
              name="search"
              placeholder="Search for a specific user"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <div className="w-full text-center text-white opacity-70">
            Search by name, email, username, or User ID
          </div>
        </div>
        {filterGroups.map(group => (
          <div key={group.name} className="mb-7">
            <div className="text-2xl underline uppercase text-center font-bold text-white">
              {group.name}
            </div>
            <div>
              {group.sections.map(section => (
                <div key={section.name} className="mb-4">
                  <div className="text-white text-lg font-semibold mb-2">{section.name}</div>
                  <div className="flex flex-wrap items-center">
                    {section.filters.map(filter => (
                      <div
                        key={filter.name}
                        className="flex items-center bg-emperorGray rounded-xl p-2 mr-4 mb-3"
                      >
                        <ProfileLabel className="text-white font-semibold mr-3">
                          {filter.label}
                        </ProfileLabel>
                        <div>
                          <Input
                            {...filter}
                            filters={filters}
                            onChange={e => handleFilterChange({ e, attribute: filter.attribute })}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
        <div className="flex justify-center mt-14">
          <Button type="submit" className="text-base bg-azureBlue h-12 w-48">
            Show results
          </Button>
        </div>
      </form>
    </div>
  )
}

export default SearchForm
